import { FC, Fragment, useMemo, useState } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import caresLogoWide from '../../assets/images/cares/logo-wide.png';
import authCover from '../../assets/images/cares/auth-cover.png';
import { getCaresShellBootstrap } from './shell/bootstrap';
import './cares-authentication.scss';

const CaresSignup: FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const csrfToken = useMemo(() => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '', []);
  const signupSecret = new URLSearchParams(window.location.search).get('dev_signup_secret') ?? '';
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  const bootstrap = getCaresShellBootstrap();
  const validationMessages = useMemo(() => Object.values(bootstrap.validationErrors ?? {}).flat(), [bootstrap.validationErrors]);
  const emailError = bootstrap.validationErrors?.email?.[0];
  const firstNameError = bootstrap.validationErrors?.first_name?.[0];
  const lastNameError = bootstrap.validationErrors?.last_name?.[0];
  const passwordError = bootstrap.validationErrors?.password?.[0];
  const passwordConfirmationError = bootstrap.validationErrors?.password_confirmation?.[0];
  const emailErrorId = emailError ? 'cares-signup-email-error' : undefined;
  const firstNameErrorId = firstNameError ? 'cares-signup-firstname-error' : undefined;
  const lastNameErrorId = lastNameError ? 'cares-signup-lastname-error' : undefined;
  const passwordErrorId = passwordError ? 'cares-signup-password-error' : undefined;
  const passwordConfirmationErrorId = passwordConfirmationError ? 'cares-signup-password-confirmation-error' : undefined;
  const [email, setEmail] = useState(() => bootstrap.authPage.email ?? '');
  const [firstName, setFirstName] = useState(() => bootstrap.authPage.first_name ?? '');
  const [lastName, setLastName] = useState(() => bootstrap.authPage.last_name ?? '');

  return (
    <Fragment>
      <Row className="authentication cares-authentication mx-0 bg-white">
        <Col xxl={5} xl={5} lg={12}>
          <Row className="justify-content-center align-items-center h-100">
            <Col xxl={6} xl={9} lg={6} md={6} sm={8} className="col-12">
              <Card className="custom-card shadow-none my-4">
                <Card.Body className="p-4">
                  <div className="mb-3 d-flex justify-content-center">
                    <Link to="/">
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-logo" />
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-dark" />
                    </Link>
                  </div>
                  <p className="h5 mb-2 text-center">Sign Up</p>
                  <p className="mb-4 text-muted op-7 fw-normal text-center">
                    Create a local CARES account for development testing. Admin access still requires a separate promotion step.
                  </p>
                  {validationMessages.length > 0 ? (
                    <Alert variant="danger" role="alert">
                      {validationMessages.map((message, index) => (
                        <div key={`${message}-${index}`}>{message}</div>
                      ))}
                    </Alert>
                  ) : null}
                  <Form method="post" action="/register">
                    <input type="hidden" name="_token" value={csrfToken} />
                    <input type="hidden" name="timezone" value={browserTimezone} />
                    {signupSecret !== '' ? <input type="hidden" name="dev_signup_secret" value={signupSecret} /> : null}
                    <Row className="gy-3">
                      <Col xl={12}>
                        <Form.Label htmlFor="cares-signup-email" className="text-default">Email Address<sup>*</sup></Form.Label>
                        <Form.Control type="email" size='lg' id="cares-signup-email" name="email" placeholder="name@example.com" value={email} onChange={(event) => setEmail(event.target.value)} isInvalid={Boolean(emailError)} aria-describedby={emailErrorId} required autoComplete="email" />
                        {emailError ? <Form.Control.Feedback id={emailErrorId} type="invalid" className="d-block">{emailError}</Form.Control.Feedback> : null}
                      </Col>
                      <Col xl={6}>
                        <Form.Label htmlFor="cares-signup-firstname" className="text-default">First Name<sup>*</sup></Form.Label>
                        <Form.Control type="text" size='lg' id="cares-signup-firstname" name="first_name" placeholder="first name" value={firstName} onChange={(event) => setFirstName(event.target.value)} isInvalid={Boolean(firstNameError)} aria-describedby={firstNameErrorId} required autoComplete="given-name" />
                        {firstNameError ? <Form.Control.Feedback id={firstNameErrorId} type="invalid" className="d-block">{firstNameError}</Form.Control.Feedback> : null}
                      </Col>
                      <Col xl={6}>
                        <Form.Label htmlFor="cares-signup-lastname" className="text-default">Last Name<sup>*</sup></Form.Label>
                        <Form.Control type="text" size='lg' id="cares-signup-lastname" name="last_name" placeholder="last name" value={lastName} onChange={(event) => setLastName(event.target.value)} isInvalid={Boolean(lastNameError)} aria-describedby={lastNameErrorId} required autoComplete="family-name" />
                        {lastNameError ? <Form.Control.Feedback id={lastNameErrorId} type="invalid" className="d-block">{lastNameError}</Form.Control.Feedback> : null}
                      </Col>
                      <Col xl={12}>
                        <Form.Label htmlFor="cares-signup-password" className="text-default">Password<sup>*</sup></Form.Label>
                        <div className="position-relative authentication-input">
                          <Form.Control type={showPassword ? 'text' : 'password'} size='lg' id="cares-signup-password" name="password" placeholder="password" isInvalid={Boolean(passwordError)} aria-describedby={passwordErrorId} required autoComplete="new-password" />
                          <button
                            type="button"
                            className="show-password-button text-muted bg-transparent border-0"
                            onClick={() => setShowPassword((value) => !value)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <i className="ri-eye-line align-middle"></i> : <i className="ri-eye-off-line align-middle"></i>}
                          </button>
                        </div>
                        {passwordError ? <Form.Control.Feedback id={passwordErrorId} type="invalid" className="d-block">{passwordError}</Form.Control.Feedback> : null}
                      </Col>
                      <Col xl={12} className="mb-2">
                        <Form.Label htmlFor="cares-signup-confirmpassword" className="text-default">Confirm Password<sup>*</sup></Form.Label>
                        <div className="position-relative authentication-input">
                          <Form.Control type={showPassword1 ? 'text' : 'password'} size='lg' id="cares-signup-confirmpassword" name="password_confirmation" placeholder="password" isInvalid={Boolean(passwordConfirmationError)} aria-describedby={passwordConfirmationErrorId} required autoComplete="new-password" />
                          <button
                            type="button"
                            className="show-password-button text-muted bg-transparent border-0"
                            onClick={() => setShowPassword1((value) => !value)}
                            aria-label={showPassword1 ? 'Hide password confirmation' : 'Show password confirmation'}
                          >
                            {showPassword1 ? <i className="ri-eye-line align-middle"></i> : <i className="ri-eye-off-line align-middle"></i>}
                          </button>
                        </div>
                        {passwordConfirmationError ? <Form.Control.Feedback id={passwordConfirmationErrorId} type="invalid" className="d-block">{passwordConfirmationError}</Form.Control.Feedback> : null}
                        <div className="form-check mt-3">
                          <input className="form-check-input" type="checkbox" value="accepted" id="cares-signup-dev-access" required />
                          <label className="form-check-label text-muted fw-normal" htmlFor="cares-signup-dev-access">
                            By creating an account you agree to local development-only access rules during the pre-SSO phase.
                          </label>
                        </div>
                      </Col>
                    </Row>
                    <div className="d-grid mt-4">
                      <button type="submit" className="btn btn-lg btn-primary">Create Account</button>
                    </div>
                  </Form>
                  <div className="text-center">
                    <p className="text-muted mt-3 mb-0">Already have an account? <a href="/login" className="text-primary">Sign In</a></p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xxl={7} xl={7} lg={7} className="d-xl-block d-none px-0">
          <div className="authentication-cover bg-primary">
            <div>
              <div className="authentication-cover-image">
                <img src={authCover} alt="CARES clinical scheduling workspace" />
              </div>
              <div className="text-center mb-4">
                <h1 className="text-fixed-white">Local Signup</h1>
              </div>
              <div className="btn-list text-center">
                <a href="/login" className="btn btn-icon authentication-cover-icon btn-wave" aria-label="Go to CARES sign in">
                  <i className="ri-login-box-line fw-bold"></i>
                </a>
                <Link to="/" className="btn btn-icon authentication-cover-icon btn-wave" aria-label="Go to CARES home">
                  <i className="ri-home-4-line fw-bold"></i>
                </Link>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

export default CaresSignup;
